import { GridColumns } from '@mui/x-data-grid';

import { ConstanstDTO } from 'interfaces/constants';

/**
 * genericTranslationColumns function
 *
 * Translates the column headers and updates the renderCell function for the status column.
 *
 * @param {Function} t The translation function that takes a key and returns the translated string.
 * @param {GridColumns} columns - Array of grid columns
 * @returns {GridColumns} Updated grid columns with translated headers and renderCell function for status column
 */
export const genericTranslationColumns = (
  t: (key: string) => string,
  columns: GridColumns
): GridColumns =>
  columns.map((elem) => {
    if (elem.headerName === 'common.commonProperties.status') {
      // eslint-disable-next-line no-param-reassign
      elem.renderCell = ({ row: { enabled } }: { row: { enabled: boolean } }) =>
        enabled
          ? t('common.commonProperties.active')
          : t('common.commonProperties.inactive');
    }
    return {
      ...elem,
      headerName: t(elem.headerName)
    };
  });

/**
 * genericTranslationConstants function
 *
 * Translates an array of constants using the provided translation function.
 *
 * @param {Function} t The translation function that takes a key and returns the translated string.
 * @param {Array} constants An array of key-value pairs representing constants to be translated
 * @returns {Object} An object with the translated constants
 */
export const genericTranslationConstants = (
  t: (key: string) => string,
  constants: { key: string; value: string }[]
): ConstanstDTO => {
  const translatedArray = constants.map(
    (elem: { key: string; value: string }) => ({
      [elem.key]: t(elem.value)
    })
  );
  let translatedObject = {};
  translatedArray.forEach((element) => {
    translatedObject = {
      ...translatedObject,
      ...element
    };
  });

  return translatedObject;
};

/**
 * genericTranslationHeaders function
 *
 * Translates the labels of the headers using the provided translation function.
 *
 * @param {Function} t The translation function that takes a key and returns the translated string.
 * @param {Array} header An array of objects containing label and key properties.
 * @returns {Array<{label: string; key: string}>}  An array of objects with translated labels.
 */
export const genericTranslationHeaders = (
  t: (key: string) => string,
  header: { label: string; key: string }[]
): Array<{ label: string; key: string }> =>
  header.map((elem) => ({
    ...elem,
    label: t(elem.label)
  }));

/**
 * genericTranslationKeysAndValuesFromArrayOfObjects function
 *
 * Translates keys and values from an array of objects using the provided translation function.
 *
 * @param {Function} t The translation function that takes a key and returns the translated string.
 * @param {Array} array The array of objects containing keys and values to be translated.
 * @returns {Array<{[key: string]: string}>} An array of objects with translated keys and values.
 */
export const genericTranslationKeysAndValuesFromArrayOfObjects = (
  t: (key: string) => string,
  array: { [key: string]: string }[]
): Array<{ [key: string]: string }> =>
  array.map((item) => {
    const translatedItem = {} as { [key: string]: string };
    Object.keys(item).forEach((key) => {
      const translatedKey = t(key);
      translatedItem[translatedKey] = t(item[key]);
    });
    return translatedItem;
  });
